import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import U340xKpiTile from '../../u340xKpiShared/components/U340xKpiTile';
import {
  U340X_KPI_MAX_MONTHS_TILE,
  type U340xKpiSeriesPoint,
  type U340xKpiTileStatus,
} from '../../u340xKpiShared/types/u340xKpiTypes';
import { NS } from '../U3400';
import { useU3400Context } from '../U3400Context';
import type { U3400TileLogicalUnitCode } from '../U3400Service';
import { useU3400DashboardHandlers } from '../hooks/useU3400DashboardHandlers';
import {
  u3400BuildMetricConfig,
  u3400ResolveTileTitle,
  u3400SeriesStatusFromLength,
} from '../utils/u3400EmbeddedTileMetrics';

interface U3400EmbeddedTileProps {
  code: U3400TileLogicalUnitCode | null;
}

function U3400EmbeddedTile({ code }: U3400EmbeddedTileProps) {
  const { t, i18n } = useTranslation(NS);
  const { initLU, getMonthsTile } = useU3400Context();
  const { fetchTileSeries, upsertMonthsTile, handleError } = useU3400DashboardHandlers();

  const months = code ? getMonthsTile(code) : U340X_KPI_MAX_MONTHS_TILE;
  const [series, setSeries] = useState<U340xKpiSeriesPoint[]>([]);
  const [status, setStatus] = useState<U340xKpiTileStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const isEn = i18n.language?.toLowerCase().startsWith('en');

  const loadSeries = useCallback(
    async (tileCode: U3400TileLogicalUnitCode, monthsCount: number) => {
      setStatus('loading');
      setErrorMessage(undefined);
      try {
        const result = await fetchTileSeries(tileCode, monthsCount);
        const nextSeries = (result?.series ?? []) as U340xKpiSeriesPoint[];
        setSeries(nextSeries);
        setStatus(u3400SeriesStatusFromLength(nextSeries.length));
      } catch (error: unknown) {
        handleError(error);
        setSeries([]);
        setStatus('error');
        setErrorMessage(t('errSeriesLoad'));
      }
    },
    [fetchTileSeries, handleError, t],
  );

  useEffect(() => {
    if (!code) return;
    void loadSeries(code, getMonthsTile(code));
  }, [code, getMonthsTile, loadSeries]);

  const title = useMemo(() => {
    if (!code) return '';
    return u3400ResolveTileTitle(code, initLU.catalogueTiles, isEn, t);
  }, [code, initLU.catalogueTiles, isEn, t]);

  const metrics = useMemo(() => (code ? u3400BuildMetricConfig(code, t) : null), [code, t]);

  const handleMonthsChange = async (nextMonths: number) => {
    if (!code) return;
    const saved = await upsertMonthsTile(code, nextMonths);
    if (saved) {
      await loadSeries(code, saved.monthsTile);
    }
  };

  if (!code) {
    return (
      <U2000Panel title={t('slot.emptyTitle')} variant="outlined" className="pt-6! min-h-88">
        <div className="flex h-64 items-center justify-center px-4 text-center">
          <p className="text-sm text-muted-foreground">{t('slot.emptyBody')}</p>
        </div>
      </U2000Panel>
    );
  }

  if (!metrics) return null;

  return (
    <U340xKpiTile
      title={title}
      size="large"
      series={series}
      primaryMetric={metrics.primaryMetric}
      secondaryMetric={metrics.secondaryMetric}
      primaryStackMetric={metrics.primaryStackMetric}
      secondaryExtraMetric={metrics.secondaryExtraMetric}
      secondaryTecMetric={metrics.secondaryTecMetric}
      secondaryTecExtraMetric={metrics.secondaryTecExtraMetric}
      months={months}
      maxMonths={U340X_KPI_MAX_MONTHS_TILE}
      status={status}
      errorMessage={errorMessage}
      onMonthsChange={(next) => {
        void handleMonthsChange(next);
      }}
      headerExtra={
        <Link
          to={`/${code}`}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline"
        >
          {t('openFullscreen')}
        </Link>
      }
    />
  );
}

export default U3400EmbeddedTile;
